/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { IRequestContext } from '@msdyn365-commerce/core';
import { observer } from 'mobx-react';
import * as React from 'react';

import { isMobile, VariantType } from '@msdyn365-commerce-modules/utilities';
import { getPayloadObject, getTelemetryAttributes, IPayLoad, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import { IIncrementalQuantityProps } from './IncrementalQuantity.props';
/**
 * IIncrementalQuantityState interface.
 */
interface IIncrementalQuantityState {
    currentInput: number;
    isUpdating: boolean;
}
/**
 * Quantity Component - This component is used to add or remove quantity.
 */
@observer
export default class IncrementalQuantity extends React.PureComponent<IIncrementalQuantityProps, IIncrementalQuantityState> {
    public static defaultProps: Partial<IIncrementalQuantityProps> = {
        min: 1,
        decrementGlyphClass: 'fas fa-minus',
        incrementGlyphClass: 'fas fa-plus'
    };

    private readonly _minValue: number;

    private readonly inputRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private readonly payLoad: IPayLoad;

    constructor(props: IIncrementalQuantityProps) {
        super(props);
        this.state = { currentInput: props.currentCount ?? 1, isUpdating: false };
        this._onIncrement = this._onIncrement.bind(this);
        this._onDecrement = this._onDecrement.bind(this);
        this._handleChange = this._handleChange.bind(this);
        this.payLoad = getPayloadObject('click', this.props.telemetryContent!, '');
        this._minValue = this.props.min ?? IncrementalQuantity.defaultProps.min!;
        // this._minValue = 0
    }

    componentDidUpdate(
        prevProps: Readonly<IIncrementalQuantityProps>,
        prevState: Readonly<IIncrementalQuantityState>,
        snapshot?: any
    ): void {
        if (prevProps.currentCount !== this.props.currentCount) {
            this.setState({ currentInput: this.props.currentCount ?? 1 });
        }
    }
    // public static getDerivedStateFromProps(props: Readonly<IIncrementalQuantityProps>, state: IIncrementalQuantityState): IIncrementalQuantityState {
    //     const nextState = { ...state };

    //     // if (props.disabled && !state.isUpdating) {
    //     //     nextState.isUpdating = true;
    //     // }

    //     // if (!props.disabled && state.isUpdating) {
    //     //     nextState.currentInput = props.currentCount ?? 1;
    //     //     nextState.isUpdating = false;
    //     // }

    //     if(this. !== props.currentCount) {
    //         nextState.currentInput = props.currentCount ?? 1;
    //     }

    //     return nextState;
    // }

    public render(): JSX.Element {
        const { min, max } = this.props;

        const glyphMinusClassName: string = `${this.props.decrementGlyphClass!} quantity__controls-glyph`;
        const glyphPlusClassName: string = `${this.props.incrementGlyphClass!} quantity__controls-glyph`;
        const decrementDisabled = this.state.currentInput <= (min ?? 1) || this.props.isGiftCard;
        const incrementDisabled = this.state.currentInput >= max || this.props.isGiftCard;
        const currentValue = this.state.currentInput;
        this.payLoad.contentAction.etext = TelemetryConstant.DecrementQuantity;
        const decrementAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        this.payLoad.contentAction.etext = TelemetryConstant.IncrementQuantity;
        const incrementAttributes = getTelemetryAttributes(this.props.telemetryContent!, this.payLoad);
        const ariaLablelText = `${this.props.inputQuantityAriaLabel ?? ''} ${this.state.currentInput}`;

        let extraClassDecrement = '';
        if (decrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassDecrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassDecrement = 'transition';
        }
        let extraClassIncrement = '';
        if (incrementDisabled) {
            // The quantity has reached its boundaries (max or min)
            extraClassIncrement = 'disabled';
        } else if (this.props.disabled) {
            // This.props.disabled shows if the state is not succeded yet
            extraClassIncrement = 'transition';
        }

        return (
            <>
                <div className='quantity' id={this.props.id}>
                    <button
                        disabled={this.props.disabled || decrementDisabled}
                        title={decrementDisabled ? '' : this.props.decrementButtonAriaLabel}
                        className={`decrement quantity__controls ${extraClassDecrement}`}
                        onClick={this._onDecrement}
                        aria-hidden={!!decrementDisabled}
                        aria-label={`${this.props.decrementButtonAriaLabel}`}
                        color='secondary'
                        {...decrementAttributes}
                    >
                        <span className={glyphMinusClassName} />
                    </button>
                    <input
                        type='number'
                        className='quantity-input'
                        pattern='[0-9]*'
                        value={this.props.currentCount}
                        onChange={this._handleChange}
                        // onBlur={this._validateMin}
                        aria-live='polite'
                        aria-label={this.isMobileView() ? ariaLablelText : `${this.props.inputQuantityAriaLabel ?? ''}`}
                        role='spinbutton'
                        aria-valuemin={this._minValue}
                        aria-valuemax={max}
                        aria-valuenow={currentValue}
                        ref={this.inputRef}
                        disabled={this.props.disabled}
                        min={this._minValue}
                        max={this.props.max}
                    />
                    <button
                        disabled={this.props.disabled || incrementDisabled}
                        title={incrementDisabled ? '' : this.props.incrementButtonAriaLabel}
                        className={`increment quantity__controls ${extraClassIncrement}`}
                        onClick={this._onIncrement}
                        aria-hidden={!!incrementDisabled}
                        aria-label={`${this.props.incrementButtonAriaLabel}`}
                        color='secondary'
                        {...incrementAttributes}
                    >
                        <span className={glyphPlusClassName} />
                    </button>
                </div>
            </>
        );
    }

    /**
     * It checks if its in mobile view or not.
     * @returns Boolean.
     */
    private readonly isMobileView = (): boolean => {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Added gridsetting values.
        const context = {
            gridSettings: {
                xs: { w: 767 },
                sm: { w: 991 },
                md: { w: 1199 },
                lg: { w: 1599 },
                xl: { w: 1600 }
            }
        } as IRequestContext;

        const isMobileCheck = isMobile({ variant: VariantType.Browser, context });
        return isMobileCheck === 'xs' || isMobileCheck === 'sm';
    };

    private _onIncrement(): void {
        let invokeCallback = false;
        const currQuantity = this.state.currentInput;
        let updatedQuantity: number;

        if (currQuantity < this.props.max) {
            invokeCallback = true;
            updatedQuantity = currQuantity + 1;
        } else {
            invokeCallback = false;
            updatedQuantity = this.props.max;
        }

        if (invokeCallback && this.props.onChange) {
            if (this.props.onChange(updatedQuantity)) {
                this.setState({ currentInput: updatedQuantity });
            }
        }
    }

    private _onDecrement(): void {
        let invokeCallback = false;
        const currQuantity = this.state.currentInput;
        let updatedQuantity: number;

        if (currQuantity >= this._minValue) {
            invokeCallback = true;
            updatedQuantity = currQuantity - 1;
        } else {
            invokeCallback = false;
            updatedQuantity = this._minValue;
        }

        if (invokeCallback && this.props.onChange) {
            if (this.props.onChange(updatedQuantity)) {
                this.setState({ currentInput: updatedQuantity });
            }
        }
    }
    private _handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
        let currentValue = Number.parseInt(e.target.value);
        if (!Number.isNaN(currentValue)) {
            currentValue = Math.min(this.props.max, Math.max(this._minValue, currentValue));
        }
        this.setState({ currentInput: currentValue }, () => this._handleUpdateState());
    }

    /**
     * Updates html element and triggers callbacks whenever state is changed.
     */
    private readonly _handleUpdateState = () => {
        if (Number.isNaN(this.state.currentInput)) {
            this.props.onChange && this.props.onChange(0);
            this.props.quantityError &&
                this.props.quantityError({
                    errorHost: 'ADDTOCART',
                    quantityError: `${this.props.invalidQuantityError}`,
                    configureErrors: {},
                    customAmountError: undefined,
                    otherError: undefined
                });
        } else {
            this.props.onChange && this.props.onChange(this.state.currentInput);
        }
        // Due of usage debouncer, we may ignore onChange return value
        const inputElement =
            this.inputRef && this.inputRef.current && this.inputRef.current instanceof HTMLInputElement && this.inputRef.current;
        if (inputElement) {
            inputElement.setAttribute('aria-valuenow', this.state.currentInput.toString());
            inputElement.setAttribute('value', this.state.currentInput.toString());
        }
    };

    /**
     * Validates min and updates.
     */
    // private readonly _validateMin = (): void => {
    //     if (Number.isNaN(this.state.currentInput)) {
    //         this.setState(
    //             { currentInput: this._minValue },
    //             this._handleUpdateState
    //         );
    //     } else {
    //         const currentValue = Math.min(this.props.max, Math.max(this._minValue, this.state.currentInput));
    //         if (currentValue !== this.state.currentInput) {
    //             this.setState(
    //                 { currentInput: currentValue },
    //                 this._handleUpdateState
    //             );
    //         } else {
    //             this._handleUpdateState();
    //         }
    //     }
    // };
}
